import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { normalizeOrgIds, pursuitStagesQueryKeys } from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { PursuitStage } from '@/types/pursuit/Stage'

const PAGE_SIZE = 10000

interface CommonProps<TResult> {
  selector?: (stages: PursuitStage[]) => TResult
  /** Multi-org only. False keeps per-org stages separate (for stage_id analytics). */
  dedupe?: boolean
}

interface SingleOrgProps<TResult> extends CommonProps<TResult> {
  orgId: UUID | ''
  orgIds?: never
}

interface MultiOrgProps<TResult> extends CommonProps<TResult> {
  orgIds: UUID[]
  orgId?: never
}

type UsePursuitStagesProps<TResult> =
  | SingleOrgProps<TResult>
  | MultiOrgProps<TResult>

interface UsePursuitStagesResult<TResult> {
  data: TResult | undefined
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  errors: Error[]
  failedOrgIds: UUID[]
  isSuccess: boolean
  refetch: () => Promise<unknown>
}

const sortByIndex = (stages: PursuitStage[]): PursuitStage[] =>
  [...stages].sort((a, b) => a.index - b.index)

// Collapses same-name stages across orgs; lowest `index` wins as canonical.
const dedupeByName = (stages: PursuitStage[]): PursuitStage[] => {
  const byName = new Map<string, PursuitStage>()
  for (const s of stages) {
    const existing = byName.get(s.name)
    if (!existing || s.index < existing.index) {
      byName.set(s.name, s)
    }
  }
  return [...byName.values()]
}

export const usePursuitStages = <TResult = PursuitStage[]>(
  args: UsePursuitStagesProps<TResult>,
): UsePursuitStagesResult<TResult> => {
  const { accessToken } = useAuthInfoWithThrow()
  const { selector, dedupe = true } = args
  const orgIds = normalizeOrgIds(args)
  const isSingle = orgIds.length <= 1
  const singleOrgId = orgIds[0] ?? ''
  const selectSingleStages = useCallback(
    (response: PaginatedResponse<PursuitStage>) => {
      const stages = sortByIndex(response.data)
      return selector ? selector(stages) : (stages as unknown as TResult)
    },
    [selector],
  )
  const selectMultiStages = useCallback(
    (response: PaginatedResponse<PursuitStage>) => {
      const raw = response.data
      const stages = sortByIndex(dedupe ? dedupeByName(raw) : raw)
      return selector ? selector(stages) : (stages as unknown as TResult)
    },
    [dedupe, selector],
  )
  const placeholderStages = useCallback(
    (previousData: PaginatedResponse<PursuitStage> | undefined) => {
      if (!previousData) return previousData
      const orgIdSet = new Set(orgIds)
      const data = previousData.data.filter((stage) =>
        orgIdSet.has(stage.organization_id),
      )
      return {
        ...previousData,
        count: data.length,
        has_more: false,
        data,
      }
    },
    [orgIds],
  )

  const singleQuery = useQuery({
    queryKey: pursuitStagesQueryKeys.organization({ orgId: singleOrgId }),
    queryFn: () =>
      fetchPursuitStages(singleOrgId, accessToken, { limit: PAGE_SIZE }),
    staleTime: Infinity,
    enabled: isSingle && !!singleOrgId && !!accessToken,
    select: selectSingleStages,
  })

  const multiQuery = useQuery({
    queryKey: pursuitStagesQueryKeys.organizationsOverview({ orgIds }),
    queryFn: () => fetchOrganizationsOverviewPursuitStages(orgIds, accessToken),
    staleTime: Infinity,
    enabled: !isSingle && orgIds.length > 0 && !!accessToken,
    placeholderData: placeholderStages,
    select: selectMultiStages,
  })

  const data = isSingle ? singleQuery.data : multiQuery.data

  if (isSingle) {
    return {
      data,
      isLoading: singleQuery.isLoading,
      isFetching: singleQuery.isFetching,
      isError: singleQuery.isError,
      errors: singleQuery.error ? [singleQuery.error as Error] : [],
      failedOrgIds: singleQuery.isError && singleOrgId ? [singleOrgId] : [],
      isSuccess: singleQuery.isSuccess,
      refetch: singleQuery.refetch,
    }
  }

  return {
    data,
    isLoading: multiQuery.isLoading,
    isFetching: multiQuery.isFetching,
    isError: multiQuery.isError,
    errors: multiQuery.error ? [multiQuery.error as Error] : [],
    failedOrgIds: multiQuery.isError ? orgIds : [],
    isSuccess: multiQuery.isSuccess,
    refetch: multiQuery.refetch,
  }
}

interface UseMinimumPursuitStageProps {
  orgId?: UUID | ''
  orgIds?: UUID[]
}

const selectMinimumStage = (
  stages: PursuitStage[],
): PursuitStage | undefined => {
  if (stages.length === 0) return undefined
  return stages.reduce((min, s) => (s.index < min.index ? s : min))
}

export const useMinimumPursuitStage = (args: UseMinimumPursuitStageProps) => {
  return usePursuitStages({
    ...(args.orgIds ? { orgIds: args.orgIds } : { orgId: args.orgId ?? '' }),
    selector: selectMinimumStage,
  } as UsePursuitStagesProps<PursuitStage | undefined>)
}

export interface TerminalStages {
  wonStage: PursuitStage | undefined
  lostStage: PursuitStage | undefined
  hasWonStage: boolean
  hasLostStage: boolean
  isMissingTerminalStages: boolean
}

const selectTerminalStages = (stages: PursuitStage[]): TerminalStages => {
  const wonStage = stages.find((s) => s.status_to_apply === 'won')
  const lostStage = stages.find((s) => s.status_to_apply === 'lost')
  return {
    wonStage,
    lostStage,
    hasWonStage: !!wonStage,
    hasLostStage: !!lostStage,
    isMissingTerminalStages: !wonStage || !lostStage,
  }
}

interface UseTerminalStagesProps {
  orgId?: UUID | ''
  orgIds?: UUID[]
}

export const useTerminalStages = (args: UseTerminalStagesProps) => {
  return usePursuitStages({
    ...(args.orgIds ? { orgIds: args.orgIds } : { orgId: args.orgId ?? '' }),
    selector: selectTerminalStages,
  } as UsePursuitStagesProps<TerminalStages>)
}

export const fetchPursuitStages = async (
  orgId: UUID | '',
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<PursuitStage>> => {
  if (accessToken === null || accessToken === undefined || !orgId) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/pursuits/stages`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'Failed to fetch pursuit stages',
      cause: res,
    })
  }
  return (await res.json()) as PaginatedResponse<PursuitStage>
}

export const fetchOrganizationsOverviewPursuitStages = async (
  orgIds: UUID[],
  accessToken: string | undefined | null,
): Promise<PaginatedResponse<PursuitStage>> => {
  if (
    accessToken === null ||
    accessToken === undefined ||
    orgIds.length === 0
  ) {
    return { count: 0, has_more: false, data: [] }
  }

  const params = new URLSearchParams()
  for (const orgId of orgIds) {
    params.append('organization_ids', orgId)
  }

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/pursuits/overview/stages?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: 'Failed to fetch overview pursuit stages',
      cause: res,
    })
  }
  return (await res.json()) as PaginatedResponse<PursuitStage>
}
